import { useState, useCallback } from 'react';
import { Combobox, Listbox, Icon, EmptySearchResult, Spinner } from '@shopify/polaris';
import { SearchIcon } from '@shopify/polaris-icons';
import { useCategoriesQuery } from './hooks/useCategoriesQuery';
import { useDebounce } from 'use-debounce';

export default function CategoryCombobox({ value = '', onChange, disabled = false, placeholder = 'Search categories' }) {
    const [inputValue, setInputValue] = useState('');
    const [debouncedInput] = useDebounce(inputValue, 300);
    const [selectedOption, setSelectedOption] = useState(value);

    // Use TanStack Query for categories, debounced
    const { data: categories = [], isLoading } = useCategoriesQuery(debouncedInput);

    const filteredOptions = categories.map((cat) => ({
        label: cat.name,
        value: cat.id,
        name: cat.name,
    }));

    const updateSelection = useCallback(
        (selected) => {
            const matchedOption = filteredOptions.find((option) => option.value === selected);
            setSelectedOption(selected);
            //setInputValue((matchedOption && matchedOption.label) || '');
            if (onChange) {
                // Pass both ID and name to the callback
                onChange({
                    id: selected,
                    name: matchedOption?.name || selected,
                });
            }
        },
        [filteredOptions, onChange]
    );

    const optionsMarkup =
        filteredOptions.length > 0
            ? filteredOptions.map(({ label, value, name }) => (
                  <Listbox.Option key={value} value={value} selected={selectedOption === value} accessibilityLabel={label}>
                      {name || label}
                  </Listbox.Option>
              ))
            : null;

    let content;
    if (isLoading) {
        content = (
            <div style={{ padding: '8px', textAlign: 'center' }}>
                <Spinner accessibilityLabel="Loading categories" size="small" />
            </div>
        );
    } else if (filteredOptions.length > 0) {
        content = <Listbox onSelect={updateSelection}>{optionsMarkup}</Listbox>;
    } else {
        content = <EmptySearchResult title="" description={`No category found matching "${inputValue}"`} />;
    }

    return (
        <Combobox
            activator={
                <Combobox.TextField
                    prefix={<Icon source={SearchIcon} />}
                    onChange={setInputValue}
                    onFocus={() => setInputValue('')}
                    label="Category"
                    labelHidden
                    value=""
                    placeholder={placeholder}
                    autoComplete="off"
                    disabled={disabled}
                />
            }
            disabled={disabled}
        >
            {content}
        </Combobox>
    );
}
